import { useContext, useMemo } from "react";

import {
  StripeRecurringInterval,
  MembershipValidityPeriod,
  getCurrencyFromCountry,
  getField,
  formatSmartNarrowCurrencyFromCents,
  Locales,
  IsoLocale,
} from "@simplyk/common";
import { Control, useWatch } from "react-hook-form";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { getDateInTimezone } from "../../../../canvas/forms-design-system/FormDate/helper";
import { useFrontendAuctionContext } from "../../../../common/contexts/FrontendAuctionContext";
import { FrontendFormContext } from "../../../../common/contexts/FrontendFormContext";
import { useLocaleContext } from "../../../../common/contexts/LocaleContext";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { deriveVariantLabel, findRateOrParent, getPreviewPurchasedNumber } from "@/common/helpers/rates";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";

interface VariantLine {
  label: string;
}

interface DescriptionData {
  rateId?: string;
  isAddOn?: boolean;
  description: string;
  price: number;
  formattedPrice: string;
  isNew: boolean;
  recurrenceExpirationInfo: string;
  variantLines?: VariantLine[];
  autoBidMaxAmount?: number;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const useTicketsDescriptions = ({ control }: { control: Control<TicketingPaymentInput> }) => {
  const { t } = useTranslate();
  let ticketsPurchased = useWatch({ control, name: "ticketsPurchased" });
  const { formObject: ticketing, isAuction, membershipRenewalBuyerInfo } = useTicketingFormV2ContextOrDefaults();
  const { commandBidsRateIdsMap, hasAlreadyBid, highestBidsMapByRateId, activeAutoBidMaxByRateId } =
    useFrontendAuctionContext();
  const { organization, stripeRecurrenceInterval } = useContext(FrontendFormContext);
  const { isPreview } = usePreviewContext();
  const { locale, isoLocale } = useLocaleContext();
  const currency = getCurrencyFromCountry(organization.country);

  if (isPreview) {
    const previewRate = ticketing.rates?.[0];
    ticketsPurchased = previewRate?.id
      ? [
          {
            rateId: previewRate.id,
            purchasedNumber: getPreviewPurchasedNumber(previewRate),
            attendeesByTicket: 1,
          },
        ]
      : [];
  }
  const displayRecurrenceInterval = useMemo(() => {
    if (!stripeRecurrenceInterval) {
      return "";
    }
    return ` / ${
      stripeRecurrenceInterval === StripeRecurringInterval.year
        ? t("common", "common.year")
        : stripeRecurrenceInterval === StripeRecurringInterval.quarter
          ? t("common", "common.quarter")
          : t("common", "common.month")
    }`;
  }, [stripeRecurrenceInterval, t]);

  const displayRecurrenceExpirationInfo = useMemo(
    () =>
      getRecurrenceExpirationInfo({
        validityPeriod: membershipRenewalBuyerInfo?.validityPeriod,
        expirationDate: membershipRenewalBuyerInfo?.expirationDate,
        stripeRecurrenceInterval,
        locale,
        t,
        timezone: ticketing.eventTimezone,
      }),
    [
      membershipRenewalBuyerInfo?.validityPeriod,
      membershipRenewalBuyerInfo?.expirationDate,
      stripeRecurrenceInterval,
      t,
      locale,
      ticketing.eventTimezone,
    ]
  );

  const ticketsDescriptions = useMemo(() => {
    if (!ticketsPurchased) {
      return [];
    }

    const parentGroupMap = new Map<
      string,
      { rate: NonNullable<ReturnType<typeof findRateOrParent>>; children: typeof ticketsPurchased }
    >();

    for (const current of ticketsPurchased) {
      if ((!current.purchasedNumber || current.purchasedNumber <= 0) && !current.bid) {
        continue;
      }
      const rate = findRateOrParent(current.rateId, ticketing.rates ?? []);
      if (!rate) {
        continue;
      }

      const parentId = rate.id;
      const existing = parentGroupMap.get(parentId);
      if (existing) {
        existing.children.push(current);
      } else {
        parentGroupMap.set(parentId, { rate, children: [current] });
      }
    }

    const result: DescriptionData[] = [];
    for (const [, { rate, children }] of parentGroupMap) {
      const rateField = getField(rate.rateFields, locale);
      const ticketAmount = rate.isPayWhatYouCan
        ? children.reduce((sum, c) => sum + (c.pwycAmount || 0), 0) /
          (children.reduce((sum, c) => sum + (c.purchasedNumber || 0), 0) || 1)
        : rate.amount;
      const totalQuantity = children.reduce((sum, c) => sum + (c.purchasedNumber || 0), 0);
      const price = (isAuction ? children[0].bid : ticketAmount * totalQuantity) || 0;
      const formattedPrice = `${formatSmartNarrowCurrencyFromCents(price, {
        locale: isoLocale,
        currency,
      })}${displayRecurrenceInterval}`;

      const variantLines: VariantLine[] = [];
      const childRateEntries = children.filter((c) => c.rateId !== rate.id);
      if (childRateEntries.length > 0) {
        for (const child of childRateEntries) {
          const label = deriveVariantLabel(child.rateId, rate, locale);
          if (label) {
            variantLines.push({ label: `${child.purchasedNumber}x ${label}` });
          }
        }
      }

      const description = `${totalQuantity}x ${rateField?.title || ""}`;
      const recurrenceExpirationInfo = rate.membershipValidityPeriod ? displayRecurrenceExpirationInfo : "";
      result.push({
        rateId: rate.id,
        isAddOn: Boolean(rate.isAddOn),
        description,
        formattedPrice,
        recurrenceExpirationInfo,
        isNew: false,
        price,
        variantLines: variantLines.length > 0 ? variantLines : undefined,
      });
    }

    return [...result.filter((line) => !line.isAddOn), ...result.filter((line) => line.isAddOn)];
  }, [
    currency,
    displayRecurrenceExpirationInfo,
    displayRecurrenceInterval,
    isAuction,
    isoLocale,
    locale,
    ticketing.rates,
    ticketsPurchased,
  ]);

  const bidsDescriptions = useMemo(() => {
    const newBidsPurchased =
      ticketsPurchased
        ?.filter((ticket) => ticket.bid && ticket.bid > 0)
        ?.map((ticket) => ({
          amount: ticket.bid,
          rateId: ticket.rateId,
          isNew: true,
          autoBidMaxAmount: ticket.autoBidMaxAmount ?? undefined,
        })) || [];

    const oldWinningBidsPurchased = commandBidsRateIdsMap
      ? Object.values(commandBidsRateIdsMap)
          ?.filter((bid) => {
            return (
              !ticketsPurchased?.find((ticket) => ticket.rateId === bid.rateId && ticket.bid && ticket.bid > 0) &&
              highestBidsMapByRateId?.[bid.rateId]?.amount === bid.amount
            );
          })
          .map((bid) => ({
            amount: bid.amount,
            rateId: bid.rateId,
            isNew: false,
            autoBidMaxAmount: activeAutoBidMaxByRateId?.[bid.rateId],
          }))
      : [];
    const allBids = [...newBidsPurchased, ...oldWinningBidsPurchased];

    return allBids.reduce<DescriptionData[]>((prev, current) => {
      const rate = ticketing.rates?.find((rate) => rate.id === current.rateId);
      if (!rate) {
        return prev;
      }
      const rateField = getField(rate.rateFields, locale);
      const price = current.amount || 0;
      const formattedPrice = formatSmartNarrowCurrencyFromCents(price, { locale: isoLocale, currency });
      const description = `${t("ticketing", "auction.payment.bidOn")} ${rateField?.title || ""}`;
      return [
        ...prev,
        {
          rateId: rate.id,
          description,
          price,
          isNew: hasAlreadyBid && current.isNew,
          recurrenceExpirationInfo: "",
          formattedPrice,
          autoBidMaxAmount: current.autoBidMaxAmount ?? undefined,
        },
      ];
    }, []);
  }, [
    activeAutoBidMaxByRateId,
    commandBidsRateIdsMap,
    currency,
    hasAlreadyBid,
    highestBidsMapByRateId,
    isoLocale,
    locale,
    t,
    ticketing.rates,
    ticketsPurchased,
  ]);

  return { ticketsDescriptions: isAuction ? bidsDescriptions : ticketsDescriptions };
};

type GetRecurrenceExpirationInfoParams = {
  validityPeriod?: MembershipValidityPeriod | null | undefined;
  expirationDate?: Date | undefined;
  stripeRecurrenceInterval?: StripeRecurringInterval | null;
  locale: Locales | IsoLocale;
  t: ReturnType<typeof useTranslate>["t"];
  timezone?: string | null;
};

const getRecurrenceExpirationInfo = ({
  validityPeriod,
  expirationDate,
  stripeRecurrenceInterval,
  locale,
  t,
  timezone,
}: GetRecurrenceExpirationInfoParams): string => {
  if (
    validityPeriod === MembershipValidityPeriod.NoExpiration ||
    validityPeriod === MembershipValidityPeriod.CustomDuration
  ) {
    return "";
  }

  const medExpirationDate = expirationDate
    ? getDateInTimezone(expirationDate, timezone).toLocaleString(
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
        { locale }
      )
    : "";
  const shortExpirationDate = expirationDate
    ? getDateInTimezone(expirationDate, timezone).toLocaleString(
        {
          year: "numeric",
          month: "long",
          day: "numeric",
        },
        { locale }
      )
    : "";

  if (!stripeRecurrenceInterval) {
    return expirationDate ? t("ticketing", "payment.summary.expires", { date: shortExpirationDate }) : "";
  }

  if (validityPeriod === MembershipValidityPeriod.AtDate) {
    return t("ticketing", "payment.summary.cancelAnyTimeWithDate", { date: medExpirationDate });
  }

  if (validityPeriod === MembershipValidityPeriod.OneYearAfterPayment) {
    return t("ticketing", "payment.summary.cancelAnyTimeYearly");
  }

  if (validityPeriod === MembershipValidityPeriod.OneMonthAfterPayment) {
    return t("ticketing", "payment.summary.cancelAnyTimeMonthly");
  }

  return t("ticketing", "payment.summary.cancelAnyTime");
};
